import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { HomeOutlined, ShoppingOutlined, UserOutlined, ShopOutlined, BuildOutlined, DatabaseOutlined, GiftOutlined, MobileOutlined, SettingOutlined, GlobalOutlined, AppstoreOutlined, NotificationOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks';
import { Role } from '@/constants/roles';
import { Permission } from '@/constants/permissions';
import { hasRole, hasAnyPermission } from '@/utils/permission';

const { Sider } = Layout;

interface MenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string;
  allowedRoles?: string[];
  requiredPermissions?: (Permission | string)[];
  children?: MenuItem[];
}

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useAppSelector((state) => state.user.userInfo);

  const baseMenuItems: MenuItem[] = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: '首页',
      requiredPermissions: [Permission.USER_MANAGE],
    },
    {
      key: 'products',
      icon: <ShopOutlined />,
      label: '商品管理',
      allowedRoles: [Role.ADMIN],
      children: [
        {
          key: '/products',
          icon: <ShopOutlined />,
          label: '商品',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/products/fabric',
          icon: <ShopOutlined />,
          label: '衬布',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/products/categories',
          icon: <ShopOutlined />,
          label: '商品分类',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/products/progress-templates',
          icon: <ShopOutlined />,
          label: '进度模板',
          allowedRoles: [Role.ADMIN],
        },
      ],
    },
    {
      key: '/workshops',
      icon: <BuildOutlined />,
      label: '加工坊管理',
      allowedRoles: [Role.ADMIN],
    },
    {
      key: '/orders',
      icon: <ShoppingOutlined />,
      label: '订单管理',
      allowedRoles: [Role.ADMIN],
    },
    {
      key: 'stock',
      icon: <DatabaseOutlined />,
      label: '库存管理',
      requiredPermissions: [Permission.STOCK_READ],
      children: [
        {
          key: '/stock/product',
          icon: <DatabaseOutlined />,
          label: '商品库存',
          requiredPermissions: [Permission.STOCK_READ],
        },
        {
          key: '/stock/variant',
          icon: <DatabaseOutlined />,
          label: '衬布库存',
          requiredPermissions: [Permission.STOCK_READ],
        },
        {
          key: '/stock/workshop',
          icon: <BuildOutlined />,
          label: '加工坊库存',
          requiredPermissions: [Permission.STOCK_READ],
        },
      ],
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: '用户管理',
      allowedRoles: [Role.ADMIN],
      children: [
        {
          key: '/users/admin',
          icon: <UserOutlined />,
          label: '管理员',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/users/workshop',
          icon: <ShopOutlined />,
          label: '加工坊',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/users/customer',
          icon: <UserOutlined />,
          label: '用户',
          allowedRoles: [Role.ADMIN],
        },
      ],
    },
    {
      key: '/growth',
      icon: <GiftOutlined />,
      label: '拓客活动',
      allowedRoles: [Role.ADMIN],
    },
    {
      key: 'miniapp',
      icon: <MobileOutlined />,
      label: '小程序管理',
      allowedRoles: [Role.ADMIN],
      children: [
        {
          key: '/miniapp/config',
          icon: <SettingOutlined />,
          label: '小程序配置',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/miniapp/home-cards',
          icon: <GlobalOutlined />,
          label: '首页卡片',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/miniapp/scrolling-lines',
          icon: <GlobalOutlined />,
          label: '商品详情',
          allowedRoles: [Role.ADMIN],
        },
        {
          key: '/miniapp/about',
          icon: <GlobalOutlined />,
          label: '关于',
          allowedRoles: [Role.ADMIN],
        },
      ],
    },
    {
      key: 'app',
      icon: <AppstoreOutlined />,
      label: 'APP管理',
      allowedRoles: [Role.ADMIN],
      children: [
        {
          key: '/app/notifications',
          icon: <NotificationOutlined />,
          label: '通知',
          allowedRoles: [Role.ADMIN],
        },
      ],
    },
    {
      key: 'settings',
      icon: <GlobalOutlined />,
      label: '全局设置',
      allowedRoles: [Role.ADMIN],
      children: [
        {
          key: '/settings',
          icon: <GlobalOutlined />,
          label: '基础设置',
          allowedRoles: [Role.ADMIN],
        },
      ],
    },
  ];

  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items
      .filter((item) => {
        if (item.allowedRoles) {
          return hasRole(userInfo, item.allowedRoles);
        }
        if (item.requiredPermissions) {
          return hasAnyPermission(userInfo, item.requiredPermissions);
        }
        return true;
      })
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter((item) => !item.children || item.children.length > 0);
  };

  const menuItems = filterMenuItems(baseMenuItems);

  return (
    <Sider collapsible>
      <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.3)' }} />
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={({ key }) => navigate(key)}
      />
    </Sider>
  );
};

export default Sidebar;
